import React, { memo } from 'react';

// TableRow component with column visibility support
interface TableRowProps {
  item: any;
  index: number;
  originalIndex: number;
  translatedStartIndex: number;
  parseRunsText: (runsString: string) => string;
  highlightedRowSid: string | null;
  dragOverRowSid: string | null;
  editingEnSid: string | null;
  editingEnText: string;
  isEnSelectionMode: boolean;
  selectedEnRows: Set<string>;
  draggedRowSid: string | null;
  selectedDocumentDestinationLanguage: string | null;
  qualityControlResults: any[];
  qualityControlDocumentId: string | null;
  selectedDocumentId: string | null;
  hoverTimeoutRef: React.MutableRefObject<NodeJS.Timeout | null>;
  isControlMode: boolean;
  handleToggleIsChecked: (sid: string) => void;
  selectedControlRows: Set<string>;
  handleControlRowSelect: (sid: string) => void;
  enChangeHistory: any[];
  onShowChangeHistory: (sid: string) => void;
  // Event handlers
  handleDragOver: (e: React.DragEvent, sid: string) => void;
  handleDragLeave: (e: React.DragEvent) => void;
  handleDrop: (e: React.DragEvent, sid: string) => void;
  handleEnEditSave: (sid: string) => void;
  handleEnEditCancel: () => void;
  setEditingEnText: (text: string) => void;
  calculateRows: (text: string) => number;
  handleEnEditStart: (sid: string, text: string) => void;
  setSelectedEnRowSid: (sid: string) => void;
  setShowDeleteEnRowModal: (show: boolean) => void;
  handleEnRowSelect: (sid: string) => void;
  handleDragStart: (e: React.DragEvent, sid: string, text: string) => void;
  handleDragEnd: () => void;
  setSelectedQualityResult: (result: any) => void;
  setShowQualityDetailPopover: (show: boolean) => void;
  setQualityPopoverPosition: (position: {top: number, left: number, arrowTop?: number, arrowDirection?: string} | null) => void;
  getLanguageName: (code: string) => string;
  showControlColumn: boolean;
  showGlossaryColumn: boolean;
  showQualityColumn: boolean;
  showCommentsColumn: boolean;
  rowComments: {[sid: string]: {user: string, userId: number, timestamp: number, comment: string}[]};
  onOpenComments: (sid: string, rowNumber: number) => void;
}

const TableRow = memo<TableRowProps>(({
  item,
  index,
  originalIndex,
  translatedStartIndex,
  parseRunsText,
  highlightedRowSid,
  dragOverRowSid,
  editingEnSid,
  editingEnText,
  isEnSelectionMode,
  selectedEnRows,
  draggedRowSid,
  selectedDocumentDestinationLanguage,
  qualityControlResults,
  qualityControlDocumentId,
  selectedDocumentId,
  hoverTimeoutRef,
  isControlMode,
  handleToggleIsChecked,
  selectedControlRows,
  handleControlRowSelect,
  enChangeHistory,
  onShowChangeHistory,
  // Event handlers
  handleDragOver,
  handleDragLeave,
  handleDrop,
  handleEnEditSave,
  handleEnEditCancel,
  setEditingEnText,
  calculateRows,
  handleEnEditStart,
  setSelectedEnRowSid,
  setShowDeleteEnRowModal,
  handleEnRowSelect,
  handleDragStart,
  handleDragEnd,
  setSelectedQualityResult,
  setShowQualityDetailPopover,
  setQualityPopoverPosition,
  getLanguageName,
  showControlColumn,
  showGlossaryColumn,
  showQualityColumn,
  showCommentsColumn,
  rowComments,
  onOpenComments
}) => {
  const hasGlossary = item.glossary_tr_regex || item.glossary_en_regex;
  const actualIndex = translatedStartIndex + index;

  // Check if this row has change history
  const hasChangeHistory = enChangeHistory?.some((historyItem: any) => historyItem.sid === item.sid && historyItem.changes && historyItem.changes.length > 0);

  // Quality control logic
  const qualityResult = qualityControlResults?.find(qr => qr.sid === item.sid);
  const hasQualityData = qualityResult && selectedDocumentId && qualityControlDocumentId === selectedDocumentId;

  // Parse violations for tooltip
  const getViolationsList = (violations: string | null): Array<{code: string, detail: string}> => {
    if (!violations) return [];
    try {
      return JSON.parse(violations);
    } catch (e) {
      return [];
    }
  };

  const translateRuleCode = (ruleCode: string): string => {
    const ruleTranslations: { [key: string]: string } = {
      // A. ANLAM / İÇERİK
      'MIS_MEAN': 'Anlam Sapması',
      'MIS_INFO': 'Bilgi Ek/Çıkartma',
      'STR_ADD': 'Yapısal Ekleme',
      'STR_DEL': 'Yapısal Silme',
      'PARA_MERGE': 'Paragraf Birleştirme',
      'PARA_SPLIT': 'Paragraf Bölme',
      'LOGIC_INVERT': 'Mantıksal Tersine Çeviri',
      'CONTEXT_MISUSE': 'Bağlam Hatası',
      // B. TERİM / TERMINOLOJİ
      'TER_GLOSS': 'Glossary\'ye Aykırı',
      'TERM_ACCURACY': 'Terminoloji Doğruluğu',
      'TER_IFRS': 'IFRS Terim Hatası',
      'TER_GRI': 'GRI Terim Hatası',
      'TER_KAP': 'KAP Terim Hatası',
      'TER_STYLE': 'Kurumsal Üslup Terimi',
      'TER_UNIT': 'Birim Terimi Hatası',
      'TER_SHORT': 'Kısaltma Hatası',
      'TER_NAME': 'Özel İsim Hatası',
      'TERM_NAME': 'Özel İsim Hatası',
      'TER_DUP': 'Terim Tutarsızlığı',
      // C. FORMAT / SAYI / TARİH
      'FMT_NUM': 'Sayı Biçimi',
      'FMT_CUR': 'Para Birimi Biçimi',
      'FMT_PCT': 'Yüzde Biçimi',
      'FMT_DATE': 'Tarih Biçimi',
      'FMT_SPACE': 'Boşluk Hatası',
      'FMT_DASH': 'Tire/En Dash Hatası',
      'FMT_PUNC': 'Noktalama Hatası',
      'FMT_CAPS': 'Büyük/Küçük Harf',
      'FMT_QUOTE': 'Tırnak Biçimi',
      'FMT_UNIT': 'Birim Biçimi',
      'FMT_TABLE': 'Tablo Biçimi',
      'FMT_LIST': 'Liste Biçimi',
      // D. ESG
      'ESG_SCOPE': 'ESG Kapsam Hatası',
      'ESG_METRIC': 'ESG Metrik Hatası',
      'ESG_TERM': 'ESG Terim Hatası',
      'ESG_FRAMEWORK': 'ESG Çerçeve Hatası',
      // E. YASAKLI / UYGUNSUZ
      'PROHIBITED': 'Yasaklı İfade',
      'LEGAL_MISUSE': 'Hukuki Atıf Hatası',
      'IFRS_MISUSE': 'IFRS Yanlış Kullanımı',
      'GRI_MISUSE': 'GRI Yanlış Kullanımı',
      'KAP_MISUSE': 'KAP Yanlış Kullanımı',
      'DATA_ALTER': 'Veri Değiştirme',
      'NAME_CHANGE': 'Yasal İsim Değişimi',
      // F. DİL / YAZIM / ÜSLUP
      'LANG_VARIANT': 'Dil Varyantı Hatası',
      'SPELLING': 'Yazım Hatası',
      'GRAMMAR': 'Dilbilgisi Hatası',
      'WORD_ORDER': 'Sözcük Sırası',
      'REGISTER': 'Üslup Uygunsuzluğu',
      'CONSISTENCY': 'Tutarlılık Sorunu',
      'REDUNDANCY': 'Gereksiz Tekrar',
      'OMISSION': 'Kelime Atlama',
      'INSERTION': 'Gereksiz Ekleme',
      'PUNCT': 'Noktalama',
      'SPACING': 'Boşluk',
      'CAPS': 'Büyük/Küçük Harf',
      // G. YAPI / BÜTÜNLÜK
      'STRUCT_ORDER': 'Sıra/Dizilim Hatası',
      'STRUCT_LOSS': 'Yapısal Kayıp',
      'STRUCT_EXTRA': 'Fazladan Yapı',
      'STRUCT_MERGE': 'Yapısal Birleştirme',
      // H. METAVERİ / TEKNİK
      'META_TAG': 'Meta Etiket Hatası',
      'LINK_BROKEN': 'Kırık Bağlantı',
      'REF_ERROR': 'Atıf/Referans Hatası',
      'VERSION_MISMATCH': 'Sürüm Uyuşmazlığı',
      'PLACEHOLDER': 'Yer Tutucu Kaldı',
      'COMMENT_LEAK': 'Yorum/Sistem Notu Sızıntısı'
    };
    return ruleTranslations[ruleCode] || ruleCode;
  };

  // Calculate dynamic width for text columns based on hidden columns
  const hiddenColumnsCount = [!showControlColumn, !showGlossaryColumn, !showQualityColumn, !showCommentsColumn].filter(Boolean).length;
  const getTextColumnWidth = () => {
    // Base width is 41.66% (5/12)
    // When columns are hidden, distribute their width equally to TR and EN columns
    if (hiddenColumnsCount === 0) return 'w-5/12'; // 41.66%
    if (hiddenColumnsCount === 1) return 'w-1/2'; // 50%
    if (hiddenColumnsCount === 2) return 'w-7/12'; // 58.33%
    if (hiddenColumnsCount === 3) return 'w-2/3'; // 66.66%
    if (hiddenColumnsCount === 4) return 'w-3/4'; // 75%
    return 'w-11/12'; // When all 5 are hidden
  };
  const textColumnWidth = getTextColumnWidth();
  
  // Comments count for this row
  const commentsCount = rowComments[item.sid]?.length || 0;

  const getScoreColor = (score: number) => {
    if (score >= 95) return 'bg-emerald-500 hover:bg-emerald-600';
    if (score >= 90) return 'bg-green-500 hover:bg-green-600';
    if (score >= 85) return 'bg-lime-500 hover:bg-lime-600';
    if (score >= 80) return 'bg-blue-500 hover:bg-blue-600';
    if (score >= 75) return 'bg-cyan-500 hover:bg-cyan-600';
    if (score >= 70) return 'bg-yellow-500 hover:bg-yellow-600';
    if (score >= 65) return 'bg-orange-500 hover:bg-orange-600';
    if (score >= 60) return 'bg-red-500 hover:bg-red-600';
    return 'bg-red-700 hover:bg-red-800';
  };

  return (
    <tr 
      key={`${item.sid}-${index}`} 
      id={`translated-row-${item.sid}`}
      className={`transition-all duration-500 divide-x divide-gray-200 dark:divide-gray-600 relative hover:z-[1001] ${
        highlightedRowSid === item.sid 
          ? 'bg-yellow-100 dark:bg-yellow-900/30 shadow-lg animate-pulse' 
          : ''
      } ${
        isControlMode && selectedControlRows.has(item.sid)
          ? 'bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500'
          : 'hover:bg-gray-50 dark:hover:bg-gray-700'
      }`}
    >
      {/* Index Column */}
      <td className="w-12 px-2 py-2 text-sm text-gray-500 dark:text-gray-400 text-center font-mono border-r border-gray-200 dark:border-gray-600">
        {originalIndex !== -1 ? originalIndex + 1 : translatedStartIndex + index + 1}
      </td>

      {/* Turkish Text Column */}
      <td className={`${textColumnWidth} px-3 py-2 text-sm text-gray-700 dark:text-gray-300 border-r border-gray-200 dark:border-gray-600`}>
        <div className="break-words">
          {parseRunsText(item.runs)}
        </div>
      </td>

      {/* English Text Column */}
      <td 
        className={`${textColumnWidth} px-3 py-2 text-sm text-gray-700 dark:text-gray-300 border-r border-gray-200 dark:border-gray-600 transition-all duration-200 ${
          dragOverRowSid === item.sid 
            ? 'bg-blue-100 dark:bg-blue-900/30 border-blue-300 dark:border-blue-600' 
            : ''
        }`}
        onDragOver={(e) => handleDragOver(e, item.sid)}
        onDragLeave={(e) => handleDragLeave(e)}
        onDrop={(e) => handleDrop(e, item.sid)}
      >
        {editingEnSid === item.sid ? (
          // English edit mode
          <div className="flex items-center gap-2">
            <div
              contentEditable
              suppressContentEditableWarning
              onInput={(e) => setEditingEnText(e.currentTarget.textContent || '')}
              className="flex-1 p-2 border-2 border-blue-500 dark:border-blue-400 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent break-words"
              style={{ minHeight: '36px' }}
            >
              {parseRunsText(item.en_runs)}
            </div>
            <div className="flex flex-col gap-1">
              <button
                onClick={() => handleEnEditSave(item.sid)}
                className="p-1.5 bg-green-600 hover:bg-green-700 text-white rounded-md transition-colors"
                title="Kaydet"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"/>
                </svg>
              </button>
              <button
                onClick={handleEnEditCancel}
                className="p-1.5 bg-red-600 hover:bg-red-700 text-white rounded-md transition-colors"
                title="İptal"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            </div>
          </div>
        ) : (
          // English view mode with drag and drop
          <div className="flex items-start justify-between group">
            <div 
              className={`flex-1 pr-2 break-words cursor-move transition-all duration-200 ${
                item.en_runs && item.en_runs.trim() !== '' && item.en_runs !== '[]'
                  ? 'hover:bg-gray-100 dark:hover:bg-gray-700 rounded p-1 -m-1'
                  : ''
              } ${
                draggedRowSid === item.sid 
                  ? 'opacity-50 bg-gray-200 dark:bg-gray-600 rounded p-1 -m-1' 
                  : ''
              }`}
              draggable={Boolean(item.en_runs && item.en_runs.trim() !== '' && item.en_runs !== '[]' && !isEnSelectionMode)}
              onDragStart={(e) => handleDragStart(e, item.sid, parseRunsText(item.en_runs) as string)}
              onDragEnd={handleDragEnd}
              title={
                item.en_runs && item.en_runs.trim() !== '' && item.en_runs !== '[]' && !isEnSelectionMode
                  ? 'Sürükleyip başka satıra taşıyabilirsiniz'
                  : undefined
              }
            >
              {parseRunsText(item.en_runs)}
              {hasChangeHistory && (
                <span className="inline-flex items-center ml-2 px-2 py-0.5 rounded-md text-xs font-semibold bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300 border border-purple-300 dark:border-purple-700">
                  <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                  Değişiklik Var
                </span>
              )}
              {item.en_runs && item.en_runs.trim() !== '' && item.en_runs !== '[]' && !isEnSelectionMode && (
                <div className="inline-flex items-center ml-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"/>
                  </svg>
                </div>
              )}
            </div>
            {!isEnSelectionMode && item.en_runs && item.en_runs.trim() !== '' && item.en_runs !== '[]' && (
              <div className="flex items-center gap-1">
                <button
                  onClick={() => onShowChangeHistory(item.sid)}
                  className="p-1.5 text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 opacity-0 group-hover:opacity-100 transition-all duration-200"
                  title="Değişiklik Geçmişi"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                </button>
                <button
                  onClick={() => handleEnEditStart(item.sid, parseRunsText(item.en_runs) as string)}
                  className="p-1.5 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 opacity-0 group-hover:opacity-100 transition-all duration-200"
                  title={`${selectedDocumentDestinationLanguage ? getLanguageName(selectedDocumentDestinationLanguage) : 'İngilizce'} Metni Düzenle`}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                  </svg>
                </button>
                <button
                  onClick={() => {
                    setSelectedEnRowSid(item.sid);
                    setShowDeleteEnRowModal(true);
                  }}
                  className="p-1.5 text-gray-400 hover:text-red-600 dark:hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all duration-200"
                  title={`Bu satırın ${selectedDocumentDestinationLanguage ? getLanguageName(selectedDocumentDestinationLanguage) : 'İngilizce'} değişikliklerini sil`}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1-1H8a1 1 0 00-1 1v3M4 7h16"/>
                  </svg>
                </button>
              </div>
            )}
          </div>
        )}
      </td>

      {/* Selection Column */}
      {isEnSelectionMode && (
        <td className="w-12 px-2 py-2 text-center border-r border-gray-200 dark:border-gray-600">
          <div className="flex items-center justify-center">
            <input
              type="checkbox"
              checked={selectedEnRows.has(item.sid)}
              onChange={() => handleEnRowSelect(item.sid)}
              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
            />
          </div>
        </td>
      )}

      {/* Control Column */}
      {showControlColumn && (
      <td 
        className={`w-20 px-3 py-3 text-center border-r border-gray-200 dark:border-gray-600 ${
          isControlMode ? 'cursor-pointer hover:bg-blue-50 dark:hover:bg-blue-900/10' : ''
        }`}
        onClick={() => {
          if (isControlMode) {
            handleControlRowSelect(item.sid);
          }
        }}
        title={isControlMode ? 'Satırı seç/seçimi kaldır' : undefined}
      >
        <div className="flex items-center justify-center gap-2">
          {isControlMode ? (
            <>
              {/* Inline Preview - Sadece seçili satırlarda göster */}
              {selectedControlRows.has(item.sid) ? (
                <div className="flex items-center gap-1" title="Kontrol durumu değişecek">
                  {/* Mevcut durum */}
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center ${
                    item.is_checked ? 'bg-emerald-500' : 'bg-amber-400'
                  }`}>
                    <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"/>
                    </svg>
                  </div>
                  
                  {/* Ok işareti */}
                  <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6"/>
                  </svg>
                  
                  {/* Yeni durum */}
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center ${
                    !item.is_checked ? 'bg-emerald-500' : 'bg-amber-400'
                  }`}>
                    <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"/>
                    </svg>
                  </div>
                </div>
              ) : (
                /* Status Toggle Button - Seçili değilse normal buton */
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    // Sadece kontrol modunda seçim yapılabilir
                    if (isControlMode) {
                      handleControlRowSelect(item.sid);
                    }
                    // Normal modda hiçbir şey yapma
                  }}
                  className={`relative w-6 h-6 rounded-full flex items-center justify-center transition-all duration-300 shadow-sm ${
                    isControlMode 
                      ? 'hover:shadow-md transform hover:scale-105 cursor-pointer' 
                      : 'cursor-default'
                  } ${
                    item.is_checked 
                      ? 'bg-gradient-to-r from-emerald-500 to-green-500 text-white' 
                      : 'bg-gradient-to-r from-amber-400 to-yellow-500 text-white'
                  } ${
                    isControlMode && item.is_checked
                      ? 'hover:from-emerald-600 hover:to-green-600'
                      : isControlMode && !item.is_checked
                        ? 'hover:from-amber-500 hover:to-yellow-600'
                        : ''
                  }`}
                  title={
                    isControlMode 
                      ? 'Satırı seç/seçimi kaldır' 
                      : (item.is_checked ? 'Kontrol edildi' : 'Kontrol edilmedi')
                  }
                >
                  <div className={`absolute inset-0 rounded-full ${item.is_checked ? 'bg-emerald-500' : 'bg-amber-400'} opacity-20 animate-pulse`}></div>
                  <svg className="w-3.5 h-3.5 relative z-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7"/>
                  </svg>
                </button>
              )}
            </>
          ) : (
            <div className="relative group">
              <div 
                className={`w-7 h-7 rounded-full flex items-center justify-center transition-all duration-300 shadow-sm ${
                  item.is_checked 
                    ? 'bg-gradient-to-r from-emerald-500 to-green-500 text-white' 
                    : 'bg-gradient-to-r from-amber-400 to-yellow-500 text-white'
                }`}
                title={item.is_checked ? 'Kontrol edildi' : 'Kontrol edilmedi'}
              >
                <div className={`absolute inset-0 rounded-full ${item.is_checked ? 'bg-emerald-500' : 'bg-amber-400'} opacity-20 animate-pulse`}></div>
                <svg className="w-4 h-4 relative z-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7"/>
                </svg>
              </div>
              
              {/* Status Badge */}
              <div className={`absolute -top-1 -right-1 w-3 h-3 rounded-full border-2 border-white dark:border-gray-800 ${
                item.is_checked ? 'bg-emerald-400' : 'bg-amber-400'
              }`}></div>
            </div>
          )}
        </div>
      </td>
      )}

      {/* Glossary Column */}
      {showGlossaryColumn && (
      <td className="w-24 px-2 py-2 text-center border-r border-gray-200 dark:border-gray-600 relative z-[1000]">
        {hasGlossary ? (
          <div className="relative group">
            <div 
              className="w-6 h-6 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto cursor-pointer hover:bg-green-200 dark:hover:bg-green-800/40 transition-colors"
              onMouseEnter={(e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                const isTopHalf = rect.top < (window.innerHeight * 1) / 2;
                const tooltip = e.currentTarget.nextElementSibling as HTMLElement;
                if (tooltip) {
                  if (isTopHalf) {
                    tooltip.classList.remove('bottom-full', 'mb-2');
                    tooltip.classList.add('top-full', 'mt-2');
                    const arrow = tooltip.querySelector('.glossary-arrow') as HTMLElement;
                    if (arrow) {
                      arrow.classList.remove('top-full', 'border-t-4', 'border-t-gray-900', 'dark:border-t-gray-700');
                      arrow.classList.add('bottom-full', 'border-b-4', 'border-b-gray-900', 'dark:border-b-gray-700');
                    }
                  } else {
                    tooltip.classList.remove('top-full', 'mt-2');
                    tooltip.classList.add('bottom-full', 'mb-2');
                    const arrow = tooltip.querySelector('.glossary-arrow') as HTMLElement;
                    if (arrow) {
                      arrow.classList.remove('bottom-full', 'border-b-4', 'border-b-gray-900', 'dark:border-b-gray-700');
                      arrow.classList.add('top-full', 'border-t-4', 'border-t-gray-900', 'dark:border-t-gray-700');
                    }
                  }
                }
              }}
            >
              <svg className="w-4 h-4 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
              </svg>
            </div>
            {/* Simplified popover for performance */}
            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-[999999] min-w-max">
              <div className="space-y-1">
                {item.glossary_tr_regex && (
                  <div className="flex items-start">
                    <span className="font-medium text-blue-300 w-6 flex-shrink-0">TR:</span>
                    <span className="ml-1">{item.glossary_tr_regex}</span>
                  </div>
                )}
                {item.glossary_en_regex && (
                  <div className="flex items-start">
                    <span className="font-medium text-green-300 w-6 flex-shrink-0">EN:</span>
                    <span className="ml-1">{item.glossary_en_regex}</span>
                  </div>
                )}
              </div>
              <div className="glossary-arrow absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900 dark:border-t-gray-700"></div>
            </div>
          </div>
        ) : (
          <span className="text-gray-600 dark:text-gray-200">-</span>
        )}
      </td>
      )}

      {/* Quality Control Column */}
      {showQualityColumn && (
      <td className="w-20 px-2 py-2 text-center border-r border-gray-200 dark:border-gray-600 relative z-[1000]">
        {hasQualityData ? (
          <div className="flex flex-col items-center">
            <div className="relative group">
              <div 
                className={`cursor-pointer w-8 h-8 rounded-full flex items-center justify-center hover:scale-110 transition-all duration-200 shadow-md ${getScoreColor(qualityResult.score)}`}
                onMouseEnter={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  const isTopHalf = rect.top < (window.innerHeight * 2) / 3;
                  const tooltip = e.currentTarget.nextElementSibling as HTMLElement;
                  if (tooltip) {
                    if (isTopHalf) {
                      tooltip.classList.remove('bottom-full', 'mb-2');
                      tooltip.classList.add('top-full', 'mt-2');
                      const arrow = tooltip.querySelector('.quality-arrow') as HTMLElement;
                      if (arrow) {
                        arrow.classList.remove('top-full', 'border-t-4', 'border-t-gray-900', 'dark:border-t-gray-700');
                        arrow.classList.add('bottom-full', 'border-b-4', 'border-b-gray-900', 'dark:border-b-gray-700');
                      }
                    } else {
                      tooltip.classList.remove('top-full', 'mt-2');
                      tooltip.classList.add('bottom-full', 'mb-2');
                      const arrow = tooltip.querySelector('.quality-arrow') as HTMLElement;
                      if (arrow) {
                        arrow.classList.remove('bottom-full', 'border-b-4', 'border-b-gray-900', 'dark:border-b-gray-700');
                        arrow.classList.add('top-full', 'border-t-4', 'border-t-gray-900', 'dark:border-t-gray-700');
                      }
                    }
                  }
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  
                  // Calculate position for modal - viewport based, not table constrained
                  const rect = e.currentTarget.getBoundingClientRect();
                  const viewportHeight = window.innerHeight;
                  const viewportWidth = window.innerWidth;
                  
                  // Padding for viewport
                  const verticalPadding = 40; // Top + bottom padding
                  const horizontalPadding = 100;
                  
                  // Modal dimensions - viewport'a sığacak şekilde
                  const maxHeight = viewportHeight - verticalPadding;
                  const maxWidth = viewportWidth - horizontalPadding;
                  
                  // Height hesaplama
                  let popoverHeight = maxHeight * 0.90; // Maksimum alanın %90'ı
                  if (viewportWidth >= 1440) {
                    popoverHeight = Math.min(maxHeight * 0.95, 900); // Büyük ekranda daha fazla
                  } else if (viewportWidth <= 1024) {
                    popoverHeight = Math.min(maxHeight * 0.85, 650); // Küçük ekranda biraz daha az
                  }
                  
                  const popoverWidth = Math.min(maxWidth, 1200); // Maksimum 1200px
                  
                  // Modalı viewport içinde tam ortala
                  const top = (viewportHeight - popoverHeight) / 2;
                  const left = (viewportWidth - popoverWidth) / 2;
                  
                  setQualityPopoverPosition({ top, left });
                  setSelectedQualityResult(qualityResult);
                  setShowQualityDetailPopover(true);
                }}
              >
                <span className="text-xs font-bold text-white pointer-events-none">
                  {Math.round(qualityResult.score)}
                </span>
              </div>

              {/* Hover Tooltip - Opens to the left */}
              {(() => {
                const violationsList = getViolationsList(qualityResult.violations);
                return violationsList.length > 0 && (
                  <div className="absolute bottom-full right-0 mb-2 w-96 px-4 py-3 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-[999999]">
                    <div className="font-semibold mb-2 text-orange-300 text-sm text-left">İhlal Detayları ({violationsList.length}):</div>
                    <div className="space-y-2.5 max-h-64 overflow-y-auto pr-1">
                      {violationsList.map((violation: {code: string, detail: string}, index: number) => (
                        <div key={index} className="border-l-4 border-orange-400 pl-3 py-1">
                          <div className="font-semibold text-orange-200 mb-1 text-left">
                            {translateRuleCode(violation.code)}
                          </div>
                          <div className="text-gray-300 text-xs leading-relaxed text-left">
                            {violation.detail}
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="quality-arrow absolute top-full right-6 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900 dark:border-t-gray-700"></div>
                  </div>
                );
              })()}
            </div>
          </div>
        ) : (
          <span className="text-gray-600 dark:text-gray-200">-</span>
        )}
      </td>
      )}

      {/* Comments Column */}
      {showCommentsColumn && (
      <td className="w-20 px-2 py-2 border-r border-gray-200 dark:border-gray-600">
        <div className="flex items-center justify-center">
          <button
            onClick={() => onOpenComments(item.sid, originalIndex + 1)}
            className="relative w-8 h-8 rounded-full bg-gradient-to-br from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white flex items-center justify-center transition-all duration-200 hover:scale-110 shadow-md group"
            title={commentsCount > 0 ? `${commentsCount} yorum var` : 'Yorum ekle'}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"/>
            </svg>
            {commentsCount > 0 && (
              <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center ring-2 ring-white dark:ring-gray-800">
                {commentsCount > 9 ? '9+' : commentsCount}
              </span>
            )}
          </button>
        </div>
      </td>
      )}

    </tr>
  );
});

TableRow.displayName = 'TableRow';

export default TableRow;
